/**
 * v2ThemeDefaults — Extracts themed default values for V2 screen editor elements
 *
 * When elements are toggled ON in the V2 editor (buttons, copy blocks, media, etc.),
 * they should receive colors/fonts from the onboarding's themeSettingsV2 snapshot
 * instead of empty `appearance: {}`.
 *
 * Each function mirrors the logic in DefaultThemeV2Mapper.applyToBasicScreen() and
 * the mapping tables in mappingTablesV2/, but returns fresh default objects
 * suitable for onChange(path, value) calls.
 *
 * Every onboarding always has theme settings — no fallback scenario needed.
 */

import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { addAlpha50 } from '@modules/themes/types/themesV2.types';

/**
 * Find the theme template for a StepKind, preferring an exact subtype match
 * (so a subtyped screen seeds from its OWN variant) and falling back to
 * first-of-kind when the subtype is absent or has no matching template. This
 * keeps legacy/no-subtype themes resolving exactly as before.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function findThemeScreen(
  screens: Record<string, unknown> | undefined,
  stepKind: string,
  subtype?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  if (!screens) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const all = Object.values(screens) as any[];
  const want = (subtype ?? '').trim();
  if (want) {
    const exact = all.find(
      (s) => s?.screenType === stepKind && (s?.subtype ?? '').trim() === want,
    );
    if (exact) return exact;
  }
  return all.find((s) => s?.screenType === stepKind);
}

// ============================================================================
// Factory Defaults (canonical structure from defaultStepNodeFactory)
// ============================================================================

/**
 * Factory default button label appearance.
 * Source: defaultButtonLabelStyle in defaultStepNodeFactory.ts
 */
const FACTORY_BUTTON_LABEL_APPEARANCE = {
  fontFamily: 'SF Pro',
  fontSize: 17,
  fontWeight: 600,
  align: 'center',
};

/**
 * Factory default primary button container appearance.
 * Source: makePrimaryButton() in defaultStepNodeFactory.ts
 * dimensions.height: 50 — default button height enabled on creation
 */
const FACTORY_PRIMARY_APPEARANCE = {
  fullWidth: true,
  borders: { radius: { uniform: 12 } },
  dimensions: { height: 50 },
};

// ============================================================================
// ActionBar Button Defaults
// ============================================================================

/**
 * Get factory defaults for a primary button (no theme required).
 * Used by theme editor where applyToBasicScreen() overlays theme colors after.
 */
export function getFactoryPrimaryButtonDefaults(
  locale: string,
  defaultLabel = 'Continue',
) {
  return {
    kind: 'label' as const,
    content: {
      translations: { [locale]: defaultLabel },
      appearance: { ...FACTORY_BUTTON_LABEL_APPEARANCE },
    },
    appearance: structuredClone(FACTORY_PRIMARY_APPEARANCE),
    action: { kind: 'next' as const, edges: [] },
  };
}

/**
 * Get factory defaults for a secondary button (no theme required).
 * Secondary: text-only, no background, fullWidth.
 */
export function getFactorySecondaryButtonDefaults(
  locale: string,
  defaultLabel = '',
) {
  return {
    kind: 'label' as const,
    content: {
      translations: { [locale]: defaultLabel },
      appearance: { ...FACTORY_BUTTON_LABEL_APPEARANCE },
    },
    appearance: { fullWidth: true, dimensions: { height: 50 } },
    action: { kind: 'next' as const, edges: [] },
  };
}

/**
 * Get themed defaults for a primary button TapTarget.
 *
 * Merges factory structural defaults (fullWidth, borderRadius, fontSize, fontWeight)
 * with theme colors (backgroundColor, text color, fontFamily).
 *
 * Also copies full appearance from theme.actionBar.primary if available
 * (preserves any extra styling like gradient, shadow set in theme).
 */
export function getThemedPrimaryButtonDefaults(
  theme: OnboardingThemeSettingsV2,
  locale: string,
  defaultLabel = 'Continue',
) {
  const { colors, fonts, actionBar } = theme;
  const themeButton = actionBar?.primary;

  // Factory structural defaults first, then overlay theme appearance
  const appearance: Record<string, unknown> = structuredClone(FACTORY_PRIMARY_APPEARANCE);
  if (themeButton?.appearance) {
    Object.assign(appearance, structuredClone(themeButton.appearance));
  }
  // Ensure backgroundColor is always from theme colors
  appearance.backgroundColor = colors.buttonsBackground;

  // Factory label defaults first, then overlay theme content appearance
  const contentAppearance: Record<string, unknown> = { ...FACTORY_BUTTON_LABEL_APPEARANCE };
  if (themeButton?.content && typeof themeButton.content === 'object' && 'appearance' in themeButton.content) {
    Object.assign(contentAppearance, structuredClone((themeButton.content as unknown as Record<string, unknown>).appearance || {}));
  }
  contentAppearance.color = colors.buttonsTitle;
  contentAppearance.fontFamily = fonts.main.fontFamily;

  return {
    kind: 'label' as const,
    content: {
      translations: { [locale]: defaultLabel },
      appearance: contentAppearance,
    },
    appearance,
    action: { kind: 'next' as const, edges: [] },
  };
}

/**
 * Get themed defaults for a secondary button TapTarget.
 *
 * Merges factory label defaults with theme colors.
 * Secondary: no background color, text color = accent (buttonsBackground).
 */
export function getThemedSecondaryButtonDefaults(
  theme: OnboardingThemeSettingsV2,
  locale: string,
  defaultLabel = '',
) {
  const { colors, fonts, actionBar } = theme;
  const themeButton = actionBar?.secondary;

  const appearance: Record<string, unknown> = { fullWidth: true, dimensions: { height: 50 } };
  if (themeButton?.appearance) {
    Object.assign(appearance, structuredClone(themeButton.appearance));
  }

  const contentAppearance: Record<string, unknown> = { ...FACTORY_BUTTON_LABEL_APPEARANCE };
  if (themeButton?.content && typeof themeButton.content === 'object' && 'appearance' in themeButton.content) {
    Object.assign(contentAppearance, structuredClone((themeButton.content as unknown as Record<string, unknown>).appearance || {}));
  }
  contentAppearance.color = colors.buttonsBackground;
  contentAppearance.fontFamily = fonts.main.fontFamily;

  return {
    kind: 'label' as const,
    content: {
      translations: { [locale]: defaultLabel },
      appearance: contentAppearance,
    },
    appearance,
    action: { kind: 'next' as const, edges: [] },
  };
}

// ============================================================================
// HeaderBar Factory Defaults
// ============================================================================

/** Factory skip label appearance (from defaultSkipLabelStyle) */
const FACTORY_SKIP_LABEL_APPEARANCE = {
  fontFamily: 'SF Pro',
  fontSize: 15,
  fontWeight: 500,
};

/**
 * Get factory defaults for skip button (no theme required).
 * Source: makeSkipButton() in defaultStepNodeFactory.ts
 */
export function getFactorySkipButtonDefaults(
  locale: string,
  defaultLabel = 'Skip',
) {
  return {
    kind: 'label' as const,
    content: {
      translations: { [locale]: defaultLabel },
      appearance: { ...FACTORY_SKIP_LABEL_APPEARANCE },
    },
    appearance: {},
    action: { kind: 'advance' as const, edges: [] },
  };
}

/**
 * Get factory defaults for back button (no theme required).
 * Source: makeBackButton() in defaultStepNodeFactory.ts
 * Uses label kind with chevron symbol for standard mobile back appearance.
 */
export function getFactoryBackButtonDefaults(locale: string, defaultLabel = '❮') {
  return {
    kind: 'label' as const,
    content: { translations: { [locale]: defaultLabel }, appearance: {} },
    appearance: {},
    action: { kind: 'retreat' as const, edges: [] },
  };
}

/**
 * Get factory defaults for close button (no theme required).
 */
export function getFactoryCloseButtonDefaults() {
  return {
    kind: 'icon' as const,
    content: { translations: {}, appearance: {} },
    appearance: {},
    action: { kind: 'complete' as const, edges: [] },
  };
}

// ============================================================================
// HeaderBar Themed Defaults
// ============================================================================

/**
 * Get themed defaults for skip button TapTarget.
 * Merges factory structure with theme colors/fonts.
 */
export function getThemedSkipButtonDefaults(
  theme: OnboardingThemeSettingsV2,
  locale: string,
  defaultLabel = 'SKIP',
) {
  const { colors, fonts } = theme;
  return {
    kind: 'label' as const,
    content: {
      translations: { [locale]: defaultLabel },
      appearance: {
        ...FACTORY_SKIP_LABEL_APPEARANCE,
        color: colors.titlesSubtitles,
        fontFamily: fonts.main.fontFamily,
      },
    },
    appearance: {},
    action: { kind: 'advance' as const, edges: [] },
  };
}

/**
 * Get themed defaults for back button.
 * Uses label kind with chevron symbol + theme color/font.
 */
export function getThemedBackButtonDefaults(
  theme: OnboardingThemeSettingsV2,
  locale: string,
  defaultLabel = '❮',
) {
  const { colors, fonts } = theme;
  return {
    kind: 'label' as const,
    content: {
      translations: { [locale]: defaultLabel },
      appearance: {
        color: colors.titlesSubtitles,
        fontFamily: fonts.main.fontFamily,
      },
    },
    appearance: {},
    action: { kind: 'retreat' as const, edges: [] },
  };
}

/**
 * Get themed defaults for close button.
 * Close button is icon-based; color = colors.titlesSubtitles.
 */
export function getThemedCloseButtonDefaults(
  theme: OnboardingThemeSettingsV2,
) {
  const { colors } = theme;
  return {
    kind: 'icon' as const,
    content: {
      translations: {},
      appearance: {
        color: colors.titlesSubtitles,
      },
    },
    appearance: {},
    action: { kind: 'complete' as const, edges: [] },
  };
}

/**
 * Get themed defaults for progress indicator.
 * activeColor = colors.buttonsBackground, trackColor = white with 30% opacity.
 */
export function getThemedProgressIndicatorDefaults(
  theme: OnboardingThemeSettingsV2,
  defaultValue = 50,
) {
  const { colors } = theme;
  return {
    value: defaultValue,
    kind: 'linearBar' as const,
    activeColor: colors.buttonsBackground,
    trackColor: '#ffffff4d',
  };
}

/**
 * Get factory defaults for progress indicator (no theme required).
 * Includes colors so they apply immediately on enable.
 */
export function getFactoryProgressIndicatorDefaults(defaultValue = 50) {
  return {
    value: defaultValue,
    kind: 'linearBar' as const,
    activeColor: '#ffc201',
    trackColor: '#ffffff',
  };
}

// ============================================================================
// CopyBlock Defaults
// ============================================================================

/**
 * Get themed defaults for title text element.
 *
 * From theme:
 * - appearance.color = colors.titlesSubtitles (100%)
 * - appearance.fontFamily = fonts.title.fontFamily (or fonts.main.fontFamily)
 * - appearance.fontSize = fonts.title.fontSize
 * - appearance.fontWeight = fonts.title.fontWeight
 * - appearance.align = fonts.title.align
 */
export function getThemedTitleDefaults(
  theme: OnboardingThemeSettingsV2,
  locale: string,
  defaultText = '',
) {
  const { colors, fonts } = theme;
  const titleFont = fonts.title || {};
  const appearance: Record<string, unknown> = {
    color: colors.titlesSubtitles,
    fontFamily: titleFont.fontFamily || fonts.main.fontFamily,
  };
  if (titleFont.fontSize != null) appearance.fontSize = titleFont.fontSize;
  if (titleFont.fontWeight != null) appearance.fontWeight = titleFont.fontWeight;
  if (titleFont.align) appearance.align = titleFont.align;

  return {
    kind: 'static' as const,
    translations: { [locale]: defaultText },
    appearance,
  };
}

/**
 * Get themed defaults for subtitle text element.
 *
 * From theme:
 * - appearance.color = colors.titlesSubtitles at 50% alpha
 * - appearance.fontFamily = fonts.subtitle.fontFamily (or fonts.main.fontFamily)
 * - appearance.fontSize = fonts.subtitle.fontSize
 * - appearance.fontWeight = fonts.subtitle.fontWeight
 * - appearance.align = fonts.subtitle.align
 */
export function getThemedSubtitleDefaults(
  theme: OnboardingThemeSettingsV2,
  locale: string,
  defaultText = '',
) {
  const { colors, fonts } = theme;
  const subtitleFont = fonts.subtitle || {};
  const appearance: Record<string, unknown> = {
    color: addAlpha50(colors.titlesSubtitles),
    fontFamily: subtitleFont.fontFamily || fonts.main.fontFamily,
  };
  if (subtitleFont.fontSize != null) appearance.fontSize = subtitleFont.fontSize;
  if (subtitleFont.fontWeight != null) appearance.fontWeight = subtitleFont.fontWeight;
  if (subtitleFont.align) appearance.align = subtitleFont.align;

  return {
    kind: 'static' as const,
    translations: { [locale]: defaultText },
    appearance,
  };
}

// ============================================================================
// TextField Defaults
// ============================================================================

/**
 * Get themed defaults for text field placeholder.
 *
 * From theme:
 * - appearance.color = colors.titlesSubtitles
 * - appearance.fontFamily = fonts.main.fontFamily
 */
export function getThemedTextFieldDefaults(
  theme: OnboardingThemeSettingsV2,
  locale: string,
  defaultText = '',
) {
  const { colors, fonts } = theme;
  return {
    kind: 'static' as const,
    translations: { [locale]: defaultText },
    appearance: {
      color: colors.titlesSubtitles,
      fontFamily: fonts.main.fontFamily,
    },
  };
}

// ============================================================================
// Media Defaults
// ============================================================================

/**
 * Get themed defaults for featured media.
 *
 * Extracts appearance settings (scaleMode, cornerRadii, dimensions) from
 * theme's HeroContent screen in flowDocument if available.
 */
export function getThemedMediaDefaults(
  theme: OnboardingThemeSettingsV2,
  subtype?: string,
) {
  const screens = theme.flowDocument?.screens;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let mediaAppearance: Record<string, unknown> = {};

  if (screens) {
    // Find HeroContent template matching the screen's subtype (else first-of-kind)
    const heroScreen = findThemeScreen(screens, 'HeroContent', subtype);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const themeFeaturedMedia = (heroScreen as any)?.content?.featuredMedia;
    if (themeFeaturedMedia?.appearance) {
      mediaAppearance = structuredClone(themeFeaturedMedia.appearance);
    }
  }

  return {
    kind: 'Image' as const,
    content: { image: { translations: {} } },
    appearance: mediaAppearance,
  };
}

// ============================================================================
// Options List Defaults
// ============================================================================

/**
 * Get themed defaults for options list appearance and item styling.
 *
 * From theme:
 * - optionsListSettings.checkboxKind → checkbox kind
 * - optionsListSettings.listBorderRadius → borders.radius.uniform
 * - optionsListSettings.itemTitle → item title font/color
 * - optionsListSettings.itemSubtitle → item subtitle font/color
 * - colors.titlesSubtitles → item title color (100%), subtitle color (50%)
 * - flowDocument ListSinglePick screen → selected/unselected bg colors, borders
 */
export function getThemedOptionsDefaults(
  theme: OnboardingThemeSettingsV2,
  subtype?: string,
) {
  const { colors, optionsListSettings, flowDocument } = theme;

  // Extract options appearance from theme's ListSinglePick screen
  const screens = flowDocument?.screens;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let optionsAppearance: Record<string, unknown> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let selectedAppearance: Record<string, unknown> = {};

  if (screens) {
    const listScreen = findThemeScreen(screens, 'ListSinglePick', subtype);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const themeOptions = (listScreen as any)?.content?.options;
    if (themeOptions?.appearance) {
      optionsAppearance = structuredClone(themeOptions.appearance);
    }
    if (themeOptions?.selectedAppearance) {
      selectedAppearance = structuredClone(themeOptions.selectedAppearance);
    }
  }

  // Item title styling from optionsListSettings
  const itemTitleAppearance: Record<string, unknown> = {
    color: colors.titlesSubtitles,
  };
  if (optionsListSettings?.itemTitle) {
    const t = optionsListSettings.itemTitle;
    if (t.fontFamily) itemTitleAppearance.fontFamily = t.fontFamily;
    if (t.fontSize != null) itemTitleAppearance.fontSize = t.fontSize;
    if (t.fontWeight != null) itemTitleAppearance.fontWeight = t.fontWeight;
  }

  // Item subtitle styling
  const itemSubtitleAppearance: Record<string, unknown> = {
    color: addAlpha50(colors.titlesSubtitles),
  };
  if (optionsListSettings?.itemSubtitle) {
    const s = optionsListSettings.itemSubtitle;
    if (s.fontFamily) itemSubtitleAppearance.fontFamily = s.fontFamily;
    if (s.fontSize != null) itemSubtitleAppearance.fontSize = s.fontSize;
    if (s.fontWeight != null) itemSubtitleAppearance.fontWeight = s.fontWeight;
  }

  return {
    checkboxKind: optionsListSettings?.checkboxKind || 'circle',
    listBorderRadius: optionsListSettings?.listBorderRadius ?? 12,
    optionsAppearance,
    selectedAppearance,
    itemTitleAppearance,
    itemSubtitleAppearance,
  };
}

// ============================================================================
// FlexibleLayout Section Defaults
// ============================================================================

/**
 * Extract themed content for a FlexibleLayout section from the theme's flowDocument.
 *
 * Maps section kinds to their source StepKind in the theme:
 * - Text → HeroContent (copy block)
 * - Button → HeroContent (actionBar.primary)
 * - PurchaseButton → Paywall (paywall.purchaseAction)
 * - OptionsList/SingleSelectionList → ListSinglePick (options)
 * - MultipleSelectionList → ListMultiPick (options)
 * - BulletList → HeroList (options)
 * - ProductCatalog → Paywall (paywall.products)
 * - PaywallHeader → Paywall (close button + header)
 * - TextField → TextInput (textField)
 * - Media → HeroContent (featuredMedia appearance only)
 */
export function getThemedFlexibleSectionContent(
  theme: OnboardingThemeSettingsV2,
  sectionKind: string,
  locale: string,
  subtype?: string,
): Record<string, unknown> | null {
  const screens = theme.flowDocument?.screens;
  if (!screens) return null;

  // Prefer a same-subtype source template, else first-of-kind.
  const findScreen = (stepKind: string) => findThemeScreen(screens, stepKind, subtype);

  try {
    switch (sectionKind) {
      case 'Text': {
        // Pure copy: clone full title from HeroContent source screen
        const hero = findScreen('HeroContent');
        const title = hero?.content?.copy?.title;
        if (title) {
          return { textContent: { title: structuredClone(title) } };
        }
        return null;
      }

      case 'Button': {
        // Pure copy: clone full TapTarget from HeroContent's actionBar
        const hero = findScreen('HeroContent');
        const primary = hero?.content?.actionBar?.primary;
        if (primary) {
          return { buttonContent: structuredClone(primary) };
        }
        return null;
      }

      case 'PurchaseButton': {
        // Pure copy: clone full purchaseAction TapTarget from Paywall source screen
        const paywall = findScreen('Paywall');
        const purchaseAction = paywall?.content?.paywall?.purchaseAction;
        if (purchaseAction) {
          return { buttonContent: structuredClone(purchaseAction) };
        }
        return null;
      }

      case 'OptionsList':
      case 'SingleSelectionList':
      case 'MultipleSelectionList':
      case 'BulletList': {
        // Pure copy: clone full OptionsList from source screen (all 14 fields)
        const sourceKindMap: Record<string, string> = {
          OptionsList: 'ListSinglePick',
          SingleSelectionList: 'ListSinglePick',
          MultipleSelectionList: 'ListMultiPick',
          BulletList: 'HeroList',
        };
        const sourceScreen = findScreen(sourceKindMap[sectionKind] || 'ListSinglePick');
        const sourceOptions = sourceScreen?.content?.options;
        if (sourceOptions) {
          const clone = structuredClone(sourceOptions);
          // BulletList: remove selectedAppearance (browse mode, no selection state)
          if (sectionKind === 'BulletList') delete clone.selectedAppearance;
          return { optionsListContent: clone };
        }
        return null;
      }

      case 'ProductCatalog': {
        const paywall = findScreen('Paywall');
        const products = paywall?.content?.paywall?.products;
        if (products) {
          return {
            productCatalogContent: structuredClone(products),
          };
        }
        return null;
      }

      case 'PaywallHeader': {
        // Pure copy: clone closeButton, headerAppearance, restoreLabel from Paywall
        const paywall = findScreen('Paywall');
        const pc = paywall?.content?.paywall;
        if (pc) {
          return {
            paywallHeaderContent: {
              closeButton: pc.closeButton ? structuredClone(pc.closeButton) : undefined,
              headerAppearance: pc.headerAppearance ? structuredClone(pc.headerAppearance) : undefined,
              restoreLabel: pc.restoreLabel ? structuredClone(pc.restoreLabel) : undefined,
            },
          };
        }
        return null;
      }

      case 'TextField': {
        // Pure copy: clone full textField from TextInput source screen
        const textInput = findScreen('TextInput');
        const textField = textInput?.content?.textField;
        if (textField) {
          return { textFieldContent: structuredClone(textField) };
        }
        return null;
      }

      case 'Media': {
        // Try Paywall first (usually has a hero image), then HeroContent
        const paywall = findScreen('Paywall');
        const hero = findScreen('HeroContent');
        const source = paywall?.content?.featuredMedia ?? hero?.content?.featuredMedia;
        if (source) {
          return { mediaContent: structuredClone(source) };
        }
        return null;
      }

      default:
        return null;
    }
  } catch {
    console.warn(`[v2ThemeDefaults] Failed to extract defaults for section kind: ${sectionKind}`);
    return null;
  }
}
